<template>
  <div class="daily-meal-plan">
    <!-- Date Header -->
    <div class="date-header">
      <button class="nav-btn" @click="previousDay">&lt;</button>
      <div class="date-info">
        <span class="date-label">{{ dateLabel }}</span>
        <span class="date-full">{{ formatDate(selectedDate) }}</span>
      </div>
      <button class="nav-btn" @click="nextDay" :disabled="isToday">&gt;</button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <span>Loading meal plan...</span>
    </div>

    <template v-else>
      <!-- Activity Context -->
      <ActivityContext v-if="dailyPlan.activity" :activity="dailyPlan.activity" class="activity-section" />

      <!-- Progress Summary -->
      <NutritionProgress :totals="dailyPlan.totals" :targets="dailyPlan.targets" :completion="dailyPlan.completion"
        :context="dailyPlan.activity?.context" :showOverall="true" class="progress-section" />

      <!-- Meals List -->
      <div class="meals-section">
        <div class="section-header">
          <h3>Meals</h3>
          <button class="add-meal-btn" @click="openMealLogger(null)">
            + Add Meal
          </button>
        </div>

        <div v-if="dailyPlan.meals.length === 0" class="no-meals">
          <span class="no-meals-icon"></span>
          <p>No meals logged yet</p>
          <button class="log-first-btn" @click="openMealLogger(null)">
            Log your first meal
          </button>
        </div>

        <div v-else class="meals-list">
          <div v-for="meal in dailyPlan.meals" :key="meal.id" class="meal-card" @click="openMealLogger(meal)">
            <div class="meal-type-badge" :style="getMealTypeStyle(meal.meal_type)">
              {{ getMealTypeSymbol(meal.meal_type) }}
            </div>
            <div class="meal-content">
              <div class="meal-header">
                <span class="meal-type-label">{{ getMealTypeLabel(meal.meal_type) }}</span>
                <span v-if="meal.meal_time" class="meal-time">{{ formatTime(meal.meal_time) }}</span>
              </div>
              <p class="meal-description">{{ meal.description || 'No description' }}</p>
              <div class="meal-macros">
                <span class="macro carbs">C: {{ meal.carbs || 0 }}g</span>
                <span class="macro protein">P: {{ meal.protein || 0 }}g</span>
                <span class="macro fat">F: {{ meal.fat || 0 }}g</span>
                <span class="macro calories">{{ meal.calories || 0 }} kcal</span>
              </div>
            </div>
            <div class="meal-edit-indicator">
              <span>Edit</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Log Buttons -->
      <div class="quick-log-section">
        <div class="section-header">
          <h3>Quick Log</h3>
        </div>
        <div class="quick-log-grid">
          <button v-for="type in quickLogTypes" :key="type.value" class="quick-log-btn"
            @click="openMealLogger(null, type.value)">
            <span class="quick-symbol" :style="{ background: type.color }">{{ type.symbol }}</span>
            <span class="quick-label">{{ type.label }}</span>
          </button>
        </div>
      </div>
    </template>

    <!-- Meal Logger Modal -->
    <div v-if="showMealLogger" class="modal-overlay" @click.self="closeMealLogger">
      <MealLogger :userId="userId" :mealDate="selectedDate" :meal="selectedMeal" :defaultMealType="defaultMealType"
        :templates="dailyPlan.suggestions || []" @saved="onMealSaved" @deleted="onMealDeleted"
        @close="closeMealLogger" />
    </div>
  </div>
</template>

<script>
import { Nutrition } from "@/services/nutrition-service";
import NutritionProgress from "./NutritionProgress.vue";
import ActivityContext from "./ActivityContext.vue";
import MealLogger from "./MealLogger.vue";

export default {
  name: "DailyMealPlan",

  components: {
    NutritionProgress,
    ActivityContext,
    MealLogger,
  },

  props: {
    userId: {
      type: [Number, String],
      required: true,
    },
    clubId: {
      type: String,
      default: null,
    },
    teamId: {
      type: [Number, String],
      default: null,
    },
    weight: {
      type: Number,
      default: null,
    },
    initialDate: {
      type: String,
      default: null,
    },
  },

  emits: ["dateChange"],

  data() {
    return {
      loading: true,
      selectedDate: this.initialDate || new Date().toISOString().split("T")[0],
      dailyPlan: {
        meals: [],
        totals: {},
        targets: {},
        completion: {},
        activity: null,
        suggestions: [],
      },
      showMealLogger: false,
      selectedMeal: null,
      defaultMealType: null,
    };
  },

  computed: {
    isToday() {
      return this.selectedDate === new Date().toISOString().split("T")[0];
    },

    dateLabel() {
      const today = new Date().toISOString().split("T")[0];
      const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];

      if (this.selectedDate === today) return "Today";
      if (this.selectedDate === yesterday) return "Yesterday";

      return new Date(this.selectedDate).toLocaleDateString("en-GB", { weekday: "long" });
    },

    quickLogTypes() {
      return [
        { value: "breakfast", symbol: "B", label: "Breakfast", color: "#ffc107" },
        { value: "lunch", symbol: "L", label: "Lunch", color: "#28a745" },
        { value: "dinner", symbol: "D", label: "Dinner", color: "#6f42c1" },
        { value: "snack", symbol: "S", label: "Snack", color: "#17a2b8" },
      ];
    },

  },

  watch: {
    selectedDate: {
      immediate: true,
      handler() {
        this.loadDailyPlan();
      },
    },
  },

  methods: {
    async loadDailyPlan() {
      this.loading = true;
      try {
        const response = await Nutrition.getDailyPlan(this.userId, this.selectedDate, {
          clubId: this.clubId,
          teamId: this.teamId,
          weight: this.weight,
        });

        if (response.data.success) {
          this.dailyPlan = response.data.data;
          // Emit activity and totals for parent components
          this.$emit("activity-loaded", this.dailyPlan.activity);
          this.$emit("totals-updated", this.dailyPlan.totals);
        }
      } catch (error) {
        console.error("Error loading daily plan:", error);
        this.$toast.error("Failed to load meal plan");
      } finally {
        this.loading = false;
      }
    },

    previousDay() {
      const date = new Date(this.selectedDate);
      date.setDate(date.getDate() - 1);
      this.selectedDate = date.toISOString().split("T")[0];
      this.$emit("dateChange", this.selectedDate);
    },

    nextDay() {
      if (this.isToday) return;
      const date = new Date(this.selectedDate);
      date.setDate(date.getDate() + 1);
      this.selectedDate = date.toISOString().split("T")[0];
      this.$emit("dateChange", this.selectedDate);
    },

    formatDate(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },

    formatTime(time) {
      if (!time) return "";
      const parts = time.split(":");
      if (parts.length >= 2) {
        const hours = parseInt(parts[0]);
        const mins = parts[1];
        const ampm = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${mins} ${ampm}`;
      }
      return time;
    },

    getMealTypeInfo(type) {
      return Nutrition.getMealTypeInfo(type);
    },

    getMealTypeSymbol(type) {
      return this.getMealTypeInfo(type).symbol;
    },

    getMealTypeLabel(type) {
      return this.getMealTypeInfo(type).label;
    },

    getMealTypeStyle(type) {
      const info = this.getMealTypeInfo(type);
      return {
        backgroundColor: info.color + "20",
        color: info.color,
      };
    },

    openMealLogger(meal, defaultType = null) {
      this.selectedMeal = meal;
      this.defaultMealType = defaultType;
      this.showMealLogger = true;
    },

    closeMealLogger() {
      this.showMealLogger = false;
      this.selectedMeal = null;
      this.defaultMealType = null;
    },

    onMealSaved() {
      this.closeMealLogger();
      this.loadDailyPlan();
    },

    onMealDeleted() {
      this.closeMealLogger();
      this.loadDailyPlan();
    },
  },
};
</script>

<style scoped lang="scss">
.daily-meal-plan {
  max-width: 600px;
  margin: 0 auto;
}

.date-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;
}

.nav-btn {
  width: 40px;
  border: none;
  background: #f0f0f0;
  border-radius: 8px;
  font-size: 1.2rem;
  cursor: pointer;
  color: #333;

  &:hover:not(:disabled) {
    background: #e0e0e0;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.date-info {
  text-align: center;
}

.date-label {
  display: block;
  font-weight: 700;
  font-size: 1.1rem;
  color: #333;
}

.date-full {
  font-size: 0.85rem;
  color: #666;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #666;

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #f0f0f0;
    border-top-color: #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 12px;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
}

.activity-section,
.progress-section {
  margin-bottom: 16px;
}

.meals-section,
.quick-log-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 16px;
  margin-bottom: 16px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  h3 {
    margin: 0;
    font-size: 1rem;
    color: #333;
  }
}

.add-meal-btn {
  padding: 6px 14px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #0056b3;
  }
}

.no-meals {
  text-align: center;
  padding: 32px 16px;
  color: #666;

  .no-meals-icon {
    display: inline-block;
    width: 48px;
    height: 48px;
    border: 3px solid #ddd;
    border-radius: 12px;
    margin-bottom: 12px;
    position: relative;

    &::before {
      content: "+";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 1.5rem;
      color: #ccc;
      font-weight: 300;
    }
  }

  p {
    margin: 0 0 16px;
  }

  .log-first-btn {
    padding: 10px 20px;
    background: #28a745;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      background: #1e7e34;
    }
  }
}

.meals-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.meal-card {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #e9ecef;

    .meal-edit-indicator {
      opacity: 1;
    }
  }
}

.meal-type-badge {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: 700;
  flex-shrink: 0;
}

.meal-content {
  flex: 1;
  min-width: 0;
}

.meal-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.meal-type-label {
  font-weight: 600;
  font-size: 0.9rem;
  color: #333;
}

.meal-time {
  font-size: 0.8rem;
  color: #666;
}

.meal-description {
  margin: 0 0 8px;
  font-size: 0.85rem;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.meal-macros {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.macro {
  font-size: 0.75rem;
  padding: 2px 6px;
  border-radius: 4px;
  background: white;
  color: #666;

  &.carbs {
    color: #ff9800;
  }

  &.protein {
    color: #dc3545;
  }

  &.fat {
    color: #28a745;
  }

  &.calories {
    color: #333;
    font-weight: 600;
  }
}

.meal-edit-indicator {
  display: flex;
  align-items: center;
  opacity: 0;
  color: #007bff;
  font-size: 0.8rem;
  transition: opacity 0.2s;
}

.quick-log-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.quick-log-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 8px;
  background: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #e9ecef;
    border-color: #007bff;
  }

  .quick-symbol {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 700;
    color: white;
  }

  .quick-label {
    font-size: 0.7rem;
    color: #666;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* Mobile adjustments */
@media screen and (max-width: 480px) {
  .date-header {
    padding: 12px;
  }

  .nav-btn {
    width: 36px;
    height: 36px;
  }

  .quick-log-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
  }

  .quick-log-btn {
    padding: 10px 4px;

    .quick-emoji {
      font-size: 1.1rem;
    }

    .quick-label {
      font-size: 0.65rem;
    }
  }

  .modal-overlay {
    padding: 0;
    align-items: flex-end;
    overflow: hidden;
  }

  .modal-overlay :deep(.meal-logger) {
    max-height: 90vh;
    overflow-y: auto;
    border-radius: 16px 16px 0 0;
    padding-bottom: env(safe-area-inset-bottom);
  }
}
</style>
